import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { Box, Chip, Stack, Typography, styled } from "@mui/material";

import { backendGet } from "@accredo/fe-shared/services/dataService.js";
import { useDebounce } from "@accredo/fe-shared/hooks/useDebounce.js";
import {
  borderRadius,
  colorsFoundation,
} from "@accredo/fe-shared/theme/index.js";

import DocumentPreview from "./DocumentPreview.jsx";
import SearchField from "./SearchField.jsx";
import { metaForType } from "../constants/documentTypeMeta.js";

const GROUPS = [
  { key: "vertrags", label: "Verträge" },
  { key: "objekts", label: "Objekte" },
  { key: "documents", label: "Dokumente" },
];

const Dropdown = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: "100%",
  left: 0,
  right: 0,
  zIndex: 20,
  marginTop: theme.spacing(0.5),
  maxHeight: "24rem",
  overflowY: "auto",
  borderRadius: borderRadius.sm,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[4],
}));

const GroupLabel = styled(Typography)(({ theme }) => ({
  padding: theme.spacing(1, 2, 0.5),
  fontSize: "0.75rem",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.grey[100],
}));

const Hit = styled("button")(({ theme }) => ({
  display: "block",
  width: "100%",
  textAlign: "left",
  background: "none",
  border: 0,
  cursor: "pointer",
  font: "inherit",
  padding: theme.spacing(1, 2),
  "&:hover": {
    backgroundColor: colorsFoundation.key.blue[50],
  },
}));

/**
 * The dashboard's cross-entity search: one text field, one grouped dropdown
 * (Verträge / Objekte / Dokumente), queried against GET /portal/search.
 * Dashboard-only - the dashboard has no single table of its own to filter,
 * unlike /vertraege, /objekte and /dokumente, where the same-looking
 * SearchField instead drives that page's own table filter[_searchword]
 * directly (see PortalContractsTable.jsx and friends).
 */
export default function GlobalSearch() {
  const navigate = useNavigate();
  const container_ref = useRef(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query, 300);
  const [results, setResults] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [previewDocument, setPreviewDocument] = useState(null);
  const [documentTypes, setDocumentTypes] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    backendGet("/data/documents/type-counts", { label: "Dokumenttypen" }).then(
      (response) => {
        setDocumentTypes(response?.document_types ?? []);
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!debouncedQuery) {
      setResults(null);
      setIsOpen(false);
      setIsSearching(false);
      return undefined;
    }

    let cancelled = false;
    setIsSearching(true);

    backendGet("/portal/search", {
      label: "Suche",
      options: { params: { q: debouncedQuery } },
    }).then((response) => {
      if (cancelled) {
        return;
      }
      setResults(response?.data ?? {});
      setIsOpen(true);
      setIsSearching(false);
    });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const closeOnOutsideClick = (event) => {
      if (!container_ref.current?.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener("click", closeOnOutsideClick);
    return () => document.removeEventListener("click", closeOnOutsideClick);
  }, [isOpen]);

  const groupsWithHits = GROUPS.filter((group) => results?.[group.key]?.length);
  const hasSearched = results !== null;
  const hasNoHits = hasSearched && groupsWithHits.length === 0;

  const selectVertrag = (hit) => {
    setIsOpen(false);
    navigate(`/vertraege/${hit.id}`);
  };

  const selectObjekt = (hit) => {
    setIsOpen(false);
    navigate(`/vertraege/${hit.vertrag.id}?objekt=${hit.id}`);
  };

  const selectDocument = (hit) => {
    setIsOpen(false);
    setPreviewDocument(hit);
  };

  return (
    <Box
      ref={container_ref}
      sx={{ position: "relative", width: "100%", mb: 3.5 }}
    >
      <SearchField
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        onFocus={() => {
          if (hasSearched) {
            setIsOpen(true);
          }
        }}
        isLoading={isSearching}
        sx={{ mb: 0 }}
      />
      {isOpen && (
        <Dropdown role="listbox">
          {hasNoHits ? (
            <Typography sx={{ p: 2 }} color="text.secondary">
              Keine Ergebnisse für „{query}“
            </Typography>
          ) : (
            groupsWithHits.map((group) => (
              <Box key={group.key}>
                <GroupLabel>{group.label}</GroupLabel>
                {results[group.key].map((hit) => {
                  if (group.key === "vertrags") {
                    return (
                      <Hit key={hit.id} onClick={() => selectVertrag(hit)}>
                        <Typography variant="body2" fontWeight={600}>
                          {hit.vertragsnummer}
                        </Typography>
                        {hit.sparten?.length > 0 && (
                          <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                            {hit.sparten.map((sparte) => (
                              <Chip
                                key={sparte.sparte}
                                label={sparte.sparte}
                                size="small"
                              />
                            ))}
                          </Stack>
                        )}
                      </Hit>
                    );
                  }

                  if (group.key === "objekts") {
                    return (
                      <Hit key={hit.id} onClick={() => selectObjekt(hit)}>
                        <Typography variant="body2" fontWeight={600}>
                          {hit.bezeichnung}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {hit.vertrag?.vertragsnummer}
                        </Typography>
                      </Hit>
                    );
                  }

                  return (
                    <Hit key={hit.id} onClick={() => selectDocument(hit)}>
                      <Typography variant="body2" fontWeight={600}>
                        {hit.bezeichnung}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {[
                          metaForType(hit.type, documentTypes).label,
                          hit.vertrag?.vertragsnummer,
                        ]
                          .filter(Boolean)
                          .join(" · ")}
                      </Typography>
                    </Hit>
                  );
                })}
              </Box>
            ))
          )}
        </Dropdown>
      )}

      <DocumentPreview
        open={Boolean(previewDocument)}
        document={previewDocument}
        onClose={() => setPreviewDocument(null)}
      />
    </Box>
  );
}
